/**
 * O aviso de que saiu versão nova enquanto a aba estava aberta.
 *
 * Quem deixa o sistema aberto o dia inteiro continua rodando o bundle da manhã
 * depois do deploy da tarde — e o bundle velho conversando com a API nova é
 * onde aparece o erro que ninguém consegue reproduzir. A aba confere o
 * `versao.json` a cada 5 minutos e sempre que volta a ficar visível.
 *
 * Não é o polling que a regra 3 do `api/query.ts` proíbe: é um arquivo estático
 * servido pelo nginx, que não chega na API nem no banco.
 *
 * Não recarrega sozinho: pode haver formulário pela metade. Avisa e espera o
 * clique. E diz **o que mudou**: o `versao.json` traz as notas do NOVIDADES.md
 * da versão publicada, e o aviso mostra as que são mais novas que a desta aba. O `reload()` basta, sem Ctrl+F5: o index.html sai com `no-store` e os
 * .js/.css têm hash no nome, então a página recarregada já vem inteira nova.
 */

import { useEffect, useState } from "react"
import { ListaDeNovidades } from "./ListaDeNovidades"
import { novidadesDesde, type NovidadesDaVersao } from "./novidades"
import { esteBuild, houveDeploy, notasValidas } from "./versao"

const CINCO_MINUTOS = 5 * 60_000

export function AvisoDeNovaVersao({
  ligado = import.meta.env.PROD,
  intervalo = CINCO_MINUTOS,
}: {
  /** Em desenvolvimento não há `versao.json`: o Vite recarrega sozinho. */
  ligado?: boolean
  intervalo?: number
}) {
  const [novaVersao, setNovaVersao] = useState(false)
  const [novas, setNovas] = useState<NovidadesDaVersao[]>([])

  useEffect(() => {
    if (!ligado || novaVersao) return
    let vivo = true

    async function conferir() {
      // Aba escondida não confere: confere quando voltar, pelo visibilitychange.
      if (document.visibilityState === "hidden") return
      try {
        const resposta = await fetch(`${import.meta.env.BASE_URL}versao.json?_=${Date.now()}`, { cache: "no-store" })
        if (!resposta.ok) return
        const publicado: unknown = await resposta.json()
        if (vivo && houveDeploy(publicado)) {
          // Só o que é mais novo que a versão desta aba. Mesmo número (um deploy sem
          // subida de versão) não tem nota nova — o aviso fica só com o pedido de atualizar.
          setNovas(novidadesDesde(notasValidas((publicado as { novidades?: unknown }).novidades), esteBuild.versao))
          setNovaVersao(true)
        }
      } catch {
        // Sem rede, ou veio HTML no lugar do JSON: tenta de novo na próxima volta.
      }
    }

    const relogio = window.setInterval(conferir, intervalo)
    const aoVoltar = () => { if (document.visibilityState === "visible") void conferir() }
    document.addEventListener("visibilitychange", aoVoltar)
    return () => {
      vivo = false
      window.clearInterval(relogio)
      document.removeEventListener("visibilitychange", aoVoltar)
    }
  }, [ligado, intervalo, novaVersao])

  if (!novaVersao) return null
  const versaoNova = novas[0]?.versao

  return (
    <div
      role="alert"
      style={{
        // Embaixo, no centro: não cobre a faixa de comando da tela nem o botão de
        // feedback, e fica acima da barra de estado (30 px) e do rodapé de versão (20 px).
        position: "fixed", left: "50%", bottom: 62, transform: "translateX(-50%)", zIndex: 1000,
        display: "flex", alignItems: novas.length ? "flex-end" : "center", gap: 14,
        width: novas.length ? 520 : undefined, maxWidth: "calc(100vw - 32px)",
        padding: "10px 12px 10px 16px", borderRadius: 10,
        background: "var(--surface)", color: "var(--txt)",
        border: "1px solid var(--line2)", borderLeft: "3px solid var(--acao)",
        boxShadow: "0 6px 24px rgba(24, 16, 32, .18)", fontSize: 13,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>
          {versaoNova ? `Saiu a versão ${versaoNova} do sistema.` : "Uma nova versão do sistema foi publicada."}
        </strong>
        {novas.length > 0 && (
          <span style={{ display: "block", margin: "8px 0 2px", maxHeight: 220, overflowY: "auto" }}>
            <ListaDeNovidades versoes={novas} limite={6} titulo={novas.length > 1} />
          </span>
        )}
        <span style={{ display: "block", fontSize: 11.5, color: "var(--txt2)", marginTop: 4 }}>
          Salve o que estiver fazendo e atualize a página.
        </span>
        {/* O aviso some ao atualizar; o rodapé fica. É lá que a pessoa volta para ler. */}
        <span style={{ display: "block", fontSize: 11, color: "var(--txt3)", marginTop: 3, lineHeight: 1.45 }}>
          Para ver o que mudou em cada versão, passe o mouse sobre <b>Sistema v…</b> ou{" "}
          <b>API v…</b> no rodapé da tela.
        </span>
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          flex: "0 0 auto", height: 32, padding: "0 14px", borderRadius: 7,
          background: "var(--acao)", color: "var(--acao-ink)", fontSize: 12.5, fontWeight: 600,
        }}
      >
        Atualizar
      </button>
    </div>
  )
}
